/* Opt-in enhancement for the existing feature-video dialogs. */
(() => {
  'use strict';
  const players = new WeakMap();
  const time = seconds => {
    const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  };
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  function attach(video, options = {}) {
    if (players.has(video)) return players.get(video);
    if (!video.parentNode) return null;
    const originalTabIndex = video.getAttribute('tabindex');
    const shell = element('div', 'pono-feature-player');
    const stage = element('div', 'pono-feature-stage');
    const cover = element('img', 'pono-feature-poster');
    cover.alt = ''; cover.setAttribute('aria-hidden', 'true');
    cover.draggable = false;
    if (video.poster) cover.src = video.poster; else cover.hidden = true;
    const applyRatio = ratio => { if (Number.isFinite(ratio) && ratio > 0) stage.style.setProperty('--pono-feature-ratio', String(ratio)); };
    const suppliedRatio = video.style.aspectRatio.split('/').map(Number);
    applyRatio(suppliedRatio[0] / (suppliedRatio[1] || 1));
    let frameCallback = null, revealFrame = null, presented = !video.poster, disposed = false;
    const bar = element('div', 'pono-feature-controls');
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Video controls');
    const makeButton = (name, className) => {
      const button = element('button', className + (options.buttonClass ? ' ' + options.buttonClass : ''), name);
      button.type = 'button';
      return button;
    };
    const play = makeButton('Play', 'pono-feature-play');
    const seek = element('input', 'pono-feature-seek');
    seek.type = 'range'; seek.min = '0'; seek.max = '1'; seek.step = '0.1'; seek.value = '0'; seek.disabled = true;
    seek.setAttribute('aria-label', 'Video position');
    const elapsed = element('span', 'pono-feature-time', '0:00 / 0:00');
    elapsed.setAttribute('aria-hidden', 'true');
    const mute = makeButton('Mute', 'pono-feature-mute');
    mute.setAttribute('aria-pressed', 'false');
    const volume = element('input', 'pono-feature-volume');
    volume.type = 'range'; volume.min = '0'; volume.max = '1'; volume.step = '0.05'; volume.value = String(video.volume);
    volume.setAttribute('aria-label', 'Volume');
    const fullscreen = makeButton('Full screen', 'pono-feature-fullscreen');
    const status = element('span', 'pono-feature-status');
    status.setAttribute('role', 'status');
    bar.append(play, seek, elapsed, mute, volume, fullscreen, status);
    const listeners = [];
    const listen = (target, event, callback) => {
      target.addEventListener(event, callback);
      listeners.push(() => target.removeEventListener(event, callback));
    };
    // Safari can clear its native poster before the video surface is ready.
    // Hold the same poster over a stable stage until a decoded frame is painted.
    const reveal = () => {
      if (presented || revealFrame !== null) return;
      revealFrame = requestAnimationFrame(() => {
        revealFrame = requestAnimationFrame(() => { cover.hidden = true; presented = true; revealFrame = null; });
      });
    };
    const watchFirstFrame = () => {
      if (presented || frameCallback !== null || !video.requestVideoFrameCallback) return;
      frameCallback = video.requestVideoFrameCallback(() => { frameCallback = null; reveal(); });
    };
    const update = () => {
      play.textContent = video.paused ? 'Play' : 'Pause';
      play.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      const duration = video.duration;
      seek.disabled = !Number.isFinite(duration) || duration <= 0;
      seek.max = seek.disabled ? '1' : String(duration);
      seek.value = String(video.currentTime || 0);
      seek.setAttribute('aria-valuetext', `${time(video.currentTime)} of ${time(duration)}`);
      elapsed.textContent = `${time(video.currentTime)} / ${time(duration)}`;
      const quiet = video.muted || video.volume === 0;
      mute.textContent = quiet ? 'Unmute' : 'Mute';
      mute.setAttribute('aria-pressed', String(quiet));
      volume.value = String(video.muted ? 0 : video.volume);
      volume.setAttribute('aria-valuetext', `${Math.round(Number(volume.value) * 100)} percent`);
    };
    const toggle = async () => {
      if (disposed) return;
      status.textContent = '';
      if (!video.paused) { video.pause(); return; }
      try { await video.play(); if (disposed) video.pause(); }
      catch { status.textContent = 'This video couldn’t start. Try again.'; }
      update();
    };
    const expand = async () => {
      status.textContent = '';
      try {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          if (document.exitFullscreen) await document.exitFullscreen();
          else document.webkitExitFullscreen?.();
        } else if (shell.requestFullscreen) await shell.requestFullscreen();
        else if (shell.webkitRequestFullscreen) shell.webkitRequestFullscreen();
        else video.webkitEnterFullscreen?.();
      } catch { status.textContent = 'Full screen isn’t available here.'; }
    };
    fullscreen.hidden = !(shell.requestFullscreen || shell.webkitRequestFullscreen || video.webkitEnterFullscreen);
    const updateFullscreen = () => {
      fullscreen.textContent = document.fullscreenElement || document.webkitFullscreenElement ? 'Exit full screen' : 'Full screen';
    };
    try {
      video.before(shell); stage.append(video, cover); shell.append(stage, bar);
      listen(cover, 'load', () => { if (!video.style.aspectRatio) applyRatio(cover.naturalWidth / cover.naturalHeight); });
      listen(cover, 'error', () => { cover.hidden = true; });
      listen(video, 'loadedmetadata', () => applyRatio(video.videoWidth / video.videoHeight));
      listen(video, 'play', watchFirstFrame);
      listen(video, 'timeupdate', () => {
        if (!video.requestVideoFrameCallback && !video.paused && video.readyState >= 2 && video.currentTime > 0) reveal();
      });
      listen(play, 'click', toggle);
      listen(video, 'click', toggle);
      listen(seek, 'input', () => { if (!seek.disabled) video.currentTime = Number(seek.value); update(); });
      listen(mute, 'click', () => { video.muted = !(video.muted || video.volume === 0); if (!video.muted && video.volume === 0) video.volume = 1; update(); });
      listen(volume, 'input', () => {
        const requested = Number(volume.value);
        video.volume = requested; video.muted = requested === 0;
        if (Math.abs(video.volume - requested) > 0.01) status.textContent = 'Use your device’s volume buttons to change the volume.';
        update();
      });
      listen(fullscreen, 'click', expand);
      for (const event of ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata', 'durationchange', 'volumechange']) listen(video, event, update);
      listen(video, 'error', () => { status.textContent = 'This video couldn’t load. Close it and try again.'; });
      listen(document, 'fullscreenchange', updateFullscreen);
      listen(document, 'webkitfullscreenchange', updateFullscreen);
      listen(video, 'keydown', event => {
        if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); toggle(); }
        else if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !seek.disabled) {
          event.preventDefault(); video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + (event.key === 'ArrowRight' ? 5 : -5)));
        }
      });
      video.tabIndex = 0;
      update();
      video.controls = false;
      const player = {destroy() {
        disposed = true;
        if (frameCallback !== null) video.cancelVideoFrameCallback?.(frameCallback);
        if (revealFrame !== null) cancelAnimationFrame(revealFrame);
        listeners.forEach(remove => remove());
        if (shell.parentNode) { shell.before(video); shell.remove(); }
        video.controls = true;
        if (originalTabIndex === null) video.removeAttribute('tabindex'); else video.setAttribute('tabindex', originalTabIndex);
        players.delete(video);
      }};
      players.set(video, player);
      return player;
    } catch {
      if (frameCallback !== null) video.cancelVideoFrameCallback?.(frameCallback);
      if (revealFrame !== null) cancelAnimationFrame(revealFrame);
      listeners.forEach(remove => remove());
      if (shell.parentNode) { shell.before(video); shell.remove(); }
      video.controls = true;
      return null;
    }
  }
  window.PonoFeaturePlayer = {attach, destroy: video => players.get(video)?.destroy()};
})();
